import React, { useCallback, useEffect, useState } from 'react';
import { Trophy, RefreshCw, Activity } from 'lucide-react';
import { adminService } from '../services/adminService';
import { formatNumber } from '../utils/format';
import './AdminPage.css';

type Tab = 'live' | 'daily' | 'weekly' | 'all-time';

const TABS: { id: Tab; label: string }[] = [
  { id: 'live', label: 'Live' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'all-time', label: 'All-time' },
];

const Leaderboard: React.FC = () => {
  const [tab, setTab] = useState<Tab>('all-time');
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const fn =
        tab === 'live' ? adminService.getLeaderboardLive
          : tab === 'daily' ? adminService.getLeaderboardDaily
            : tab === 'weekly' ? adminService.getLeaderboardWeekly
              : adminService.getLeaderboardAllTime;
      const res = await fn(100);
      // Normalise: backend may return array, {entries: []}, or {data: []}
      const items = Array.isArray(res) ? res : res?.entries || res?.items || res?.data || [];
      setData(items);
      setMeta(Array.isArray(res) ? null : res);
    } catch (e) {
      console.warn('leaderboard load failed', e);
      setData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Trophy size={20} style={{ marginRight: 8 }} />Leaderboard / Vault Protocol</h1>
          <p className="page-subtitle">Verifier rankings across live, daily, weekly and all-time windows</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <div style={{ display: 'flex', gap: 4 }}>
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`btn ${tab === t.id ? '' : 'secondary'}`}
                style={{ padding: '6px 12px' }}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {meta?.last_updated && (
            <span className="kpi-detail">
              <Activity size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Updated {meta.last_updated}
            </span>
          )}
        </div>
        <div className="panel-body tight">
          {loading ? <div className="skeleton" style={{ height: 240, margin: 16 }} /> : data.length === 0 ? (
            <div className="empty">No leaderboard entries for this window</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th><th>User</th><th>Country</th><th>Score</th><th>Earnings</th>
                </tr>
              </thead>
              <tbody>
                {data.map((u: any, i: number) => (
                  <tr key={u.user_id || u.id || i}>
                    <td>
                      <span className="pill" style={{
                        background: i === 0 ? '#fef3c7' : i === 1 ? '#e0e7ff' : i === 2 ? '#fef9c3' : undefined,
                        color: i === 0 ? '#b45309' : i === 1 ? '#3730a3' : i === 2 ? '#a16207' : undefined,
                      }}>#{u.rank ?? i + 1}</span>
                    </td>
                    <td>{u.full_name || u.email || u.username || `User ${u.user_id || u.id}`}</td>
                    <td>{u.country_code || u.country || '—'}</td>
                    <td>{formatNumber(u.score ?? u.points ?? 0)}</td>
                    <td>{formatNumber(u.earnings ?? u.earned_balance ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
